/**
 * ═══════════════════════════════════════════════════════════════════════════
 * ApplicationsTab.tsx - Tab de Aplicaciones con Sistema de Ventanas
 * ═══════════════════════════════════════════════════════════════════════════
 * 
 * @description Launcher de aplicaciones con ventanas flotantes arrastrables
 * @version 1.1.0
 * @architecture WASP Frontend
 * 
 * FUNCIONALIDADES:
 * - ✅ Launcher de aplicaciones
 * - ✅ Ventanas flotantes arrastrables
 * - ✅ Sistema de minimización a burbujas
 * - ✅ Fullscreen y resize
 * - ✅ Soporte dark/light theme
 * - ✅ Persistencia de estado (próximamente)
 * 
 * APLICACIONES DISPONIBLES:
 * - ✅ Envío Masivo (En desarrollo)
 * - ✅ Preformato de Mensajes (En desarrollo)
 * - ⏳ Programación (Próximamente)
 * - ⏳ Analytics (Próximamente)
 * 
 * ═══════════════════════════════════════════════════════════════════════════
 */

import React from 'react';
import { Container, Card, Row, Col, Badge } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faPaperPlane, 
  faFileAlt, 
  faClock, 
  faChartLine,
  faRocket,
  faWrench,
  faCogs,
  faDoorOpen
} from '@fortawesome/free-solid-svg-icons';
import { useWindowManager, WindowPosition } from '../hooks/useWindowManager';
import ApplicationWindow from '../components/ApplicationWindow';
import ApplicationBubble from '../components/ApplicationBubble';
import BulkSendApp from '../components/applications/BulkSendApp';
import PerformanceApp from '../components/PerformanceApp';
import BulkSendTemplates from '../components/applications/BulkSendTemplates';
import AdvancedConfigApp from '../components/applications/AdvancedConfigApp';
import StarGateApp from '../components/applications/StarGateApp';
import SchedulerApp from '../components/WASP/Scheduler/SchedulerApp';

interface ApplicationsTabProps {
  language?: 'es' | 'en';
}

interface AppFeature {
  id: string;
  icon: any;
  title: string;
  description: string;
  status: 'available' | 'planned' | 'future' | 'buy' | 'development';
  color: string;
  component?: React.ReactNode;
}

export const ApplicationsTab: React.FC<ApplicationsTabProps> = ({
  language = 'es'
}) => {
  const {
    windows,
    openWindow,
    closeWindow,
    minimizeWindow,
    maximizeWindow,
    updateWindowPosition,
    updateWindowSize,
    bringToFront,
    getWindow
  } = useWindowManager();

  const features: AppFeature[] = [
    {
      id: 'bulk-send',
      icon: faPaperPlane,
      title: 'Envío Masivo',
      description: 'Envía mensajes a múltiples contactos simultáneamente',
      status: 'available',
      color: '#007bff',
      component: (
        <BulkSendApp onSendMessage={() => {}} />
      )
    },
    {
      id: 'templates',
      icon: faFileAlt,
      title: 'Preformato de Mensajes',
      description: 'Crea y gestiona templates de mensajes reutilizables',
      status: 'available',
      color: '#28a745',
      component: (
        <BulkSendTemplates 
          onUseTemplate={(template) => {
          // 1. Guardar en localStorage PRIMERO
          localStorage.setItem('bulksend:pendingTemplate', JSON.stringify(template));
  
          // 2. Emitir evento
          window.dispatchEvent(new CustomEvent('bulksend:loadTemplate', {
          detail: { template, switchToComposer: true }
          }));
  
          // 3. Minimizar ventana Templates
          const templatesWindow = windows.find(w => w.conversationId === 'templates');
          if (templatesWindow) {
            minimizeWindow(templatesWindow.id);
          }
  
          // 4. Abrir BulkSendApp
            openWindow('bulk-send');
          }}
        />
      )
    },
    {
      id: 'stargate',
      icon: faDoorOpen, // o faPortal si existe
      title: 'StarGate',
      description: 'Portal unificado a todas tus conversaciones multi-canal',
      status: 'buy',
      color: '#0ea5e9', // Azul portal/cosmic
      component: <StarGateApp />
    },
    {
      id: 'advanced-config',
      icon: faCogs,
      title: 'Configuración Avanzada',
      description: 'Gestiona canales, webhooks y monitoreo del sistema',
      status: 'buy',
      color: '#6366f1',
      component: <AdvancedConfigApp />
    },
    {
      id: 'scheduler',
      icon: faClock,
      title: 'Programación',
      description: 'Programa el envío de mensajes para más tarde',
      status: 'available',
      color: '#ffc107',
      component: <SchedulerApp />
    },
    {
      id: 'analytics',
      icon: faChartLine,
      title: 'Performance Metrics',
      description: 'Dashboard avanzado de métricas de WhatsApp en tiempo real',
      status: 'buy',
      color: '#0d6efd',
      component: <PerformanceApp />
    }
  ];

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'available':
        return <Badge bg="success">Disponible</Badge>;
      case 'planned':
        return <Badge bg="warning" text="dark">En Desarrollo</Badge>;
      case 'development':
        return <Badge bg="info" text="light">En Desarrollo</Badge>;
      case 'future':
        return <Badge bg="secondary">Próximamente</Badge>;
      case 'buy':
        return <Badge bg="danger">Comprar</Badge>;
      default:
        return <Badge bg="info">Nuevo</Badge>;
    }
  };

  const handleFeatureClick = (feature: AppFeature) => {
    if (feature.status === 'available' || feature.status === 'development'  && feature.component) {
      openWindow(feature.id);
    }
  };

  return (
    <div
      className="applications-tab-container"
      style={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: 'var(--bs-body-bg)',
        padding: '20px',
        position: 'relative'
      }}
    >
      <Container fluid>
        <div className="text-center mb-5" style={{ paddingTop: '10px' }}>
          <div style={{ fontSize: '2rem', marginBottom: '10px' }}>
            <FontAwesomeIcon icon={faRocket} style={{ color: '#007bff' }} />
          </div>
          <h3 style={{ color: '#007bff', fontWeight: 600, marginBottom: '16px' }}>
            Aplicaciones WASP
          </h3>
          {/*<p style={{ color: 'var(--bs-secondary)', fontSize: '1.1rem', maxWidth: '600px', margin: '0 auto' }}>
            Herramientas avanzadas para potenciar tu gestión de WhatsApp
          </p>*/}
        </div>

        <Row className="g-4">
          {features.map((feature, index) => (
            <Col key={index} md={6} lg={3}>
              <Card
                className="feature-card h-100"
                onClick={() => handleFeatureClick(feature)}
                style={{
                  border: 'none',
                  borderRadius: '12px',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                  transition: 'all 0.3s ease',
                  cursor: feature.status === 'available' || feature.status === 'development' ? 'pointer' : 'not-allowed',
                  opacity: feature.status === 'available' || feature.status === 'development' ? 1 : 0.7,
                  backgroundColor: 'var(--bs-body-bg)'
                }}
              >
                <Card.Body className="text-center p-4">
                  <div
                    style={{
                      width: '60px',
                      height: '60px',
                      borderRadius: '50%',
                      backgroundColor: `${feature.color}20`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      margin: '0 auto 20px',
                      fontSize: '1.8rem',
                      color: feature.color
                    }}
                  >
                    <FontAwesomeIcon icon={feature.icon} />
                  </div>
                  
                  <h5 style={{ color: 'var(--bs-body-color)', fontWeight: 600, marginBottom: '12px' }}>
                    {feature.title}
                  </h5>
                  
                  <p style={{ color: 'var(--bs-secondary)', fontSize: '0.9rem', marginBottom: '16px' }}>
                    {feature.description}
                  </p>
                  
                  {getStatusBadge(feature.status)}
                </Card.Body>
              </Card>
            </Col>
          ))}
        </Row>

        {/*<Row className="mt-5">
          <Col lg={8} className="mx-auto">
            <Card
              style={{
                border: 'none',
                borderRadius: '12px',
                backgroundColor: 'rgba(0, 123, 255, 0.1)',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
              }}
            >
              <Card.Body className="p-4">
                <div className="d-flex align-items-start gap-3">
                  <div style={{ fontSize: '2rem', color: '#007bff' }}>
                    <FontAwesomeIcon icon={faWrench} />
                  </div>
                  <div>
                    <h5 style={{ color: '#007bff', fontWeight: 600, marginBottom: '8px' }}>
                      Sistema de Ventanas Activo
                    </h5>
                    <p style={{ color: 'var(--bs-body-color)', marginBottom: '12px', fontSize: '0.95rem' }}>
                      Haz clic en <strong>Envío Masivo</strong>, <strong>Preformato de Mensajes</strong> o <strong>Performance Metrics</strong> para 
                      abrir ventanas flotantes. Puedes arrastrarlas, redimensionarlas, minimizarlas o ponerlas 
                      en pantalla completa.
                    </p>
                    <p style={{ color: 'var(--bs-secondary)', marginBottom: 0, fontSize: '0.9rem' }}>
                      <strong>Tip:</strong> Las aplicaciones minimizadas aparecen como burbujas flotantes que 
                      puedes arrastrar por la pantalla.
                    </p>
                  </div>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        <Row className="mt-5">
          <Col lg={10} className="mx-auto">
            <h4 className="text-center mb-4" style={{ color: 'var(--bs-body-color)', fontWeight: 600 }}>
              Hoja de Ruta
            </h4>
            
            <div className="timeline">
              <div className="timeline-item mb-4">
                <div className="d-flex align-items-start gap-3">
                  <div 
                    style={{
                      width: '40px',
                      height: '40px',
                      borderRadius: '50%',
                      backgroundColor: '#28a745',
                      color: '#fff',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontWeight: 'bold',
                      flexShrink: 0
                    }}
                  >
                    1
                  </div>
                  <div>
                    <h6 style={{ color: 'var(--bs-body-color)', fontWeight: 600 }}>
                      Fase 1: Envío Masivo <Badge bg="success" className="ms-2">Disponible</Badge>
                    </h6>
                    <p style={{ color: 'var(--bs-secondary)', fontSize: '0.9rem', marginBottom: 0 }}>
                      Sistema de envío masivo con selección de contactos, validación y seguimiento.
                    </p>
                  </div>
                </div>
              </div>

              <div className="timeline-item mb-4">
                <div className="d-flex align-items-start gap-3">
                  <div 
                    style={{
                      width: '40px',
                      height: '40px',
                      borderRadius: '50%',
                      backgroundColor: '#28a745',
                      color: '#fff',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontWeight: 'bold',
                      flexShrink: 0
                    }}
                  >
                    2
                  </div>
                  <div>
                    <h6 style={{ color: 'var(--bs-body-color)', fontWeight: 600 }}>
                      Fase 2: Templates <Badge bg="success" className="ms-2">Disponible</Badge>
                    </h6>
                    <p style={{ color: 'var(--bs-secondary)', fontSize: '0.9rem', marginBottom: 0 }}>
                      Editor de templates con variables dinámicas y previsualización.
                    </p>
                  </div>
                </div>
              </div>

              <div className="timeline-item mb-4">
                <div className="d-flex align-items-start gap-3">
                  <div 
                    style={{
                      width: '40px',
                      height: '40px',
                      borderRadius: '50%',
                      backgroundColor: '#6c757d',
                      color: '#fff',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontWeight: 'bold',
                      flexShrink: 0
                    }}
                  >
                    3
                  </div>
                  <div>
                    <h6 style={{ color: 'var(--bs-body-color)', fontWeight: 600 }}>
                      Fase 3: Programación <Badge bg="secondary" className="ms-2">Próximamente</Badge>
                    </h6>
                    <p style={{ color: 'var(--bs-secondary)', fontSize: '0.9rem', marginBottom: 0 }}>
                      Calendario de envíos y automatización de campañas.
                    </p>
                  </div>
                </div>
              </div>

              <div className="timeline-item">
                <div className="d-flex align-items-start gap-3">
                  <div 
                    style={{
                      width: '40px',
                      height: '40px',
                      borderRadius: '50%',
                      backgroundColor: '#28a745',
                      color: '#fff',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontWeight: 'bold',
                      flexShrink: 0
                    }}
                  >
                    4
                  </div>
                  <div>
                    <h6 style={{ color: 'var(--bs-body-color)', fontWeight: 600 }}>
                      Fase 4: Performance Metrics <Badge bg="success" className="ms-2">Disponible</Badge>
                    </h6>
                    <p style={{ color: 'var(--bs-secondary)', fontSize: '0.9rem', marginBottom: 0 }}>
                      Dashboard avanzado con métricas en tiempo real, gráficos y export de reportes.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </Col>
        </Row>*/}
      </Container>

      {windows.filter(w => !w.isMinimized).map(window => {
        const feature = features.find(f => f.id === window.conversationId);
        if (!feature) return null;

        return (
          <ApplicationWindow
            key={window.id}
            id={window.id}
            appId={window.conversationId}
            appName={feature.title}
            appIcon={feature.icon}
            position={window.position}
            size={window.size}
            zIndex={window.zIndex}
            onClose={() => closeWindow(window.id)}
            onMinimize={() => minimizeWindow(window.id)}
            onPositionChange={(pos) => updateWindowPosition(window.id, pos)}
            onSizeChange={(size) => updateWindowSize(window.id, size)}
            onBringToFront={() => bringToFront(window.id)}
          >
            {feature.component}
          </ApplicationWindow>
        );
      })}

      {windows.filter(w => w.isMinimized).map(window => {
        const feature = features.find(f => f.id === window.conversationId);
        if (!feature) return null;

        return (
          <ApplicationBubble
            key={window.id}
            id={window.id}
            appId={window.conversationId}
            appName={feature.title}
            appIcon={feature.icon}
            position={window.position}
            zIndex={window.zIndex}
            onMaximize={() => maximizeWindow(window.id)}
            onClose={() => closeWindow(window.id)}
            onDrag={(pos: WindowPosition) => updateWindowPosition(window.id, pos)}
            onBringToFront={() => bringToFront(window.id)}
          />
        );
      })}

      <style>{`
        .applications-tab-container {
          animation: fadeIn 0.3s ease-out;
        }

        @keyframes fadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }

        .feature-card {
          animation: cardSlideIn 0.5s ease-out backwards;
        }

        .feature-card:nth-child(1) { animation-delay: 0.1s; }
        .feature-card:nth-child(2) { animation-delay: 0.2s; }
        .feature-card:nth-child(3) { animation-delay: 0.3s; }
        .feature-card:nth-child(4) { animation-delay: 0.4s; }

        @keyframes cardSlideIn {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .feature-card:hover {
          transform: translateY(-5px);
          box-shadow: 0 6px 16px rgba(0,0,0,0.15) !important;
        }

        .timeline-item {
          animation: timelineFadeIn 0.5s ease-out backwards;
        }

        .timeline-item:nth-child(1) { animation-delay: 0.5s; }
        .timeline-item:nth-child(2) { animation-delay: 0.6s; }
        .timeline-item:nth-child(3) { animation-delay: 0.7s; }
        .timeline-item:nth-child(4) { animation-delay: 0.8s; }

        @keyframes timelineFadeIn {
          from {
            opacity: 0;
            transform: translateX(-20px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }

        @media (max-width: 768px) {
          .applications-tab-container {
            padding: 12px !important;
          }

          .feature-card .card-body {
            padding: 20px !important;
          }
        }
      `}</style>
    </div>
  );
};

export default ApplicationsTab;